/**
 * ============================================================================
 * INPUT COMPONENT - Responsive & Accessible
 * ============================================================================
 */

import React, { forwardRef } from 'react';

/**
 * Input Component
 * @param {Object} props
 * @param {string} props.type - Input type
 * @param {string} props.value - Input value
 * @param {Function} props.onChange - Change handler
 * @param {string} props.placeholder - Placeholder text
 * @param {string} props.error - Error message
 * @param {string} props.className - Additional classes
 * @param {boolean} props.required - Required field
 * @param {string} props.label - Label text
 * @param {string} props.id - Input ID
 * @param {string} props.name - Input name
 * @returns {JSX.Element}
 */
const Input = forwardRef(
  (
    {
      type = 'text',
      value,
      onChange,
      placeholder,
      error,
      className = '',
      required = false,
      label,
      id,
      name,
      ...props
    },
    ref
  ) => {
    /* LinkedIn/Cursor: 1px #D1D5DB, focus #0A66C2, padding 10px 14px, radius 6px, placeholder #9CA3AF */
    const baseClasses =
      'w-full px-3.5 py-2.5 border rounded-md bg-white text-[#111827] placeholder-[#9CA3AF] focus:outline-none focus:ring-2 focus:ring-[#0A66C2]/20 focus:border-[#0A66C2] transition-all duration-200 text-sm';
    
    const errorClasses = error
      ? 'border-[#EF4444] focus:ring-[#EF4444]/20 focus:border-[#EF4444]'
      : 'border-[#D1D5DB]';

    const classes = `${baseClasses} ${errorClasses} ${className}`.trim();

    const inputId = id || name;

    return (
      <div className="w-full">
        {label && (
          <label
            htmlFor={inputId}
            className="block text-sm font-medium text-[#111827] mb-1.5"
          >
            {label}
            {required && <span className="text-[#EF4444] ml-1">*</span>}
          </label>
        )}
        <input
          ref={ref}
          type={type}
          id={inputId}
          name={name}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          required={required}
          className={classes}
          aria-invalid={!!error}
          aria-describedby={error ? `${inputId}-error` : undefined}
          {...props}
        />
        {error && (
          <p
            id={`${inputId}-error`}
            className="mt-1 text-sm text-[#DC2626]"
            role="alert"
          >
            {error}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

export default Input;
